import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/common/PrivateRoute';
import './styles/App.css';

// Layout Component
import Layout from './components/layout/Layout';

// Auth Components
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ProfileSetup from './components/auth/ProfileSetup';

// Main Components
import Dashboard from './components/dashboard/Dashboard';
import AlignPage from './components/matches/AlignPage';
import Messages from './components/messages/Messages';
import Chat from './components/messages/Chat';
import Profile from './components/profile/Profile';
import Settings from './components/profile/Settings';
import UserProfile from './components/profile/UserProfile';

// Common Components
import Loading from './components/common/Loading';

// Main app home (side-by-side dashboard/messages)
import MainPage from './components/layout/MainPage';

// NEW: Public landing + subscription + placeholders
import Landing from './components/landing/Landing';
import Subscriptions from './components/subscriptions/Subscriptions';
import PlaceholderPage from './components/common/PlaceholderPage';

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Persisted theme (light/dark) across ALL pages
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const theme = savedTheme === 'dark' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <Router>
      <AuthProvider>
        <div className="app">
          <Routes>
            {/* ✅ Public Landing (NO redirect to login anymore) */}
            <Route path="/" element={<Landing />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/profile-setup" element={<ProfileSetup />} />

            {/* ✅ Subscription page (Upgrade now goes here) */}
            <Route path="/subscriptions" element={<Subscriptions />} />

            {/* ✅ Protected App Home */}
            <Route
              path="/app"
              element={
                <PrivateRoute>
                  <MainPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/matches"
              element={
                <PrivateRoute>
                  <Layout>
                    <AlignPage />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Messages */}
            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <Layout>
                    <Messages />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/:conversationId"
              element={
                <PrivateRoute>
                  <Layout>
                    <Chat />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Chat Routes */}
            <Route
              path="/chat/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <Chat />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/chat/:conversationId"
              element={
                <PrivateRoute>
                  <Layout>
                    <Chat />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Profile */}
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/user/:userId"
              element={
                <PrivateRoute>
                  <Layout>
                    <UserProfile />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* ✅ Requested routes so nav clicks actually work */}
            <Route
              path="/events"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Events" subtitle="Discover upcoming hangouts and meetups." />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Favorites" subtitle="Saved profiles you liked." />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/likes"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Likes" subtitle="People who liked you." />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/visitors"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Visitors" subtitle="See who viewed your profile." />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/gifts"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Gifts" subtitle="Send and receive gifts." />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/help"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Help" subtitle="Support and FAQs." />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/search"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlaceholderPage title="Search" subtitle="Search results will appear here." />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Any unknown route => landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;